import React, { useEffect, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'

import Navbar from '../../components/NewNavigation/Navbar'
import Sidenav from '../../components/NewNavigation/SideNav'
import Html from '../../components/Html/Html'
import Layout from '../../components/Layout'
import Footnotes from '../../components/Footnotes/FundClassLegalText'
import StandardHero from '../../components/Hero/StandardHero'
import FundDocuments from '../../templateComponents/funds/FundDocuments'
import FundManagement from '../../templateComponents/funds/FundManagement'
import FundOverview from '../../templateComponents/funds/FundOverview'
import FundPerformance from '../../templateComponents/funds/FundPerformance'
import FundSnapshot from '../../templateComponents/funds/FundSnapshot'
import FundSplash from '../../templateComponents/funds/FundSplash'
import { findField } from '../../utils/seo-utils'

import { createNavbarSections } from './utils'

// Styled Components
const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  columnGap: '24px',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    columnGap: 0,
    maxWidth: '100vw',
    padding: '0 14px',
  },
}))

const SectionsContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '0 16px',
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    minWidth: 'unset',
  },
}))

const SideNavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
}))

const FundDetailTemplateComponent = ({
  data,
  footerDisclaimer,
  location,
  pageContext,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'))
  const [selected, setSelected] = useState(undefined)

  const { meta_title, meta_keywords, meta_description } = data?.seo || {}
  const {
    ticker: fundTicker,
    share_class: productShareClass,
    share_class_content: shareClassContent,
  } = data?.fund_classes?.find(({ ticker }) =>
    location.pathname.includes(`/${ticker.toLowerCase()}`)
  ) || {}

  const currentTicker = data.ticker || fundTicker
  const shareClassTitle = productShareClass?.[0]?.title

  const tailoredShareholderReports =
    data?.documents?.tailored_shareholder_report || []
  const tailoredShareholderReport = tailoredShareholderReports.find(
    (report) =>
      report?.share_class?.toLowerCase() === 'all' ||
      (shareClassTitle &&
        report?.share_class?.toLowerCase() ===
          shareClassTitle.toLowerCase().trim().split(' ')[0])
  )

  const shouldShowTailoredShareholderReport = Boolean(tailoredShareholderReport)

  const semiAnnualTailoredShareholderReports =
    data?.documents?.semi_annual_tailored_shareholder_report || []

  const semiAnnualTailoredShareholderReport =
    semiAnnualTailoredShareholderReports.find(
      (report) =>
        report?.share_class?.toLowerCase() === 'all' ||
        report?.share_class?.toLowerCase() ===
          shareClassTitle.toLowerCase().trim().split(' ')[0]
    )

  const shouldShowSemiAnnualTailoredShareholderReport = Boolean(
    semiAnnualTailoredShareholderReport
  )

  const filteredProductDocuments = {
    ...data.documents,
    additional_documents: (data.documents.additional_documents || []).filter(
      ({ share_class }) =>
        share_class === 'ETF' ||
        share_class === 'all' ||
        (shareClassTitle &&
          shareClassTitle
            .toLowerCase()
            .trim()
            .split(' ')[0]
            .includes(share_class?.toLowerCase()))
    ),
    tailored_shareholder_report: shouldShowTailoredShareholderReport
      ? tailoredShareholderReport
      : undefined,
    semi_annual_tailored_shareholder_report:
      shouldShowSemiAnnualTailoredShareholderReport
        ? semiAnnualTailoredShareholderReport
        : undefined,
  }
  const productIcon = data?.product_icon
  const title = meta_title || data?.title
  const seoTitleWithTicker = `${title} | ${currentTicker}`
  const heroTitleWithTicker = data.title
  const description = meta_description || title

  const showOverview = findField(data, 'navigation_and_headline')

  const navbarSections = createNavbarSections(
    data.product_tabs,
    data.fund_classes,
    true
  ).filter((section) => {
    if (!section.name || section.name.trim() === '') return false
    if (section.name === 'Overview' && !showOverview) return false
    return true
  })

  const investmentTeamTitle =
    data.product_tabs?.find((tab) => tab.management_section)?.management_section
      ?.title || ''

  const relatedArticles = data.product_tabs?.find((tab) => tab.insights_list)
    ?.insights_list?.article_reference

  const fundPreviewSection = data.product_tabs?.find(
    (tab) => tab.overview_section
  )

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (location.hash) {
        const index = navbarSections.findIndex(
          (section) => `#${section.id}` === location.hash
        )
        setSelected(index)

        const el = document.querySelector(location.hash)
        if (el) {
          window.scrollTo({
            top: el.getBoundingClientRect().y + window.scrollY - 50,
            left: 0,
            behavior: 'smooth',
          })
        }
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      }
    }
  }, [location.hash, navbarSections])

  const seo = findField(data, 'seo')
  const ograph = findField(data, 'open_graph')

  return (
    <Layout
      seo={seo}
      ograph={ograph}
      headerBackgroundColor="#092A48"
      footerDisclaimer={footerDisclaimer}
    >
      <StandardHero
        height={336}
        smHeight={478}
        pageContext={pageContext}
        title={heroTitleWithTicker}
        subtitle={data.hero_text}
        ticker={currentTicker}
        data={data.hero[0]}
        productIcon={productIcon}
      />
      {fundPreviewSection && (
        <FundSnapshot
          section_title={fundPreviewSection?.overview_section.title}
          overview_section={fundPreviewSection.overview_section}
          pageContext={pageContext}
          fund_classes={data.fund_classes}
          productType={data.product_type}
          id="FundSnapshot"
          fundTitle={data.title}
        />
      )}
      <ContentContainer>
        <SideNavContainer>
          {isMobile ? (
            <Navbar
              initialIndex={selected}
              sections={navbarSections}
              fund_classes={
                pageContext.fundClass?.product_type.toLowerCase() !== 'etf' &&
                data.fund_classes
              }
              shareclass={pageContext?.fundClass?.title}
              subscription={data.subscription_category}
            />
          ) : (
            <Sidenav
              initialIndex={selected}
              sections={navbarSections}
              fund_classes={
                pageContext.fundClass?.product_type.toLowerCase() !== 'etf' &&
                data.fund_classes
              }
              shareclass={pageContext?.fundClass?.title}
              subscription={data?.subscription_category}
              relatedArticles={relatedArticles}
            />
          )}
        </SideNavContainer>
        <SectionsContainer>
          {data.product_tabs?.map((section, index) => {
            const uniqueKey = section.id || section.title || index
            const showOverviewTitle =
              section?.overview_section?.navigation_and_headline
            if (section.product_splash) {
              return (
                <React.Fragment key={uniqueKey}>
                  <FundSplash
                    splashSection={section?.product_splash}
                    id={section?.product_splash?.title?.replace(/\s/g, '')}
                    index={index}
                    relatedArticles={data.insightArticles}
                  />
                  {shareClassContent && <Html content={shareClassContent} />}
                </React.Fragment>
              )
            }

            if (section.overview_section) {
              return (
                <FundOverview
                  key={uniqueKey}
                  index={index}
                  showOverviewTitle={showOverviewTitle}
                  overview_section={section.overview_section}
                  fund_classes={data.fund_classes}
                  pageContext={pageContext}
                  id={section.overview_section.title.replace(/\s/g, '')}
                  management_section_title={investmentTeamTitle}
                  managers={data.product_subadviser?.[0]?.managers}
                  investmentTeamLogo={
                    data.product_subadviser?.[0]?.subadviser_firm?.[0]?.logo
                  }
                  investmentTeamLink={
                    data.product_subadviser?.[0]?.subadviser_firm?.[0]?.url
                  }
                  html={section.overview_section.html}
                  inceptionDate={data.inception_date}
                  fundTitle={data.title}
                />
              )
            }

            if (section.management_section) {
              return (
                <FundManagement
                  key={uniqueKey}
                  index={index}
                  management_section={section?.management_section}
                  product_subadviser={data.product_subadviser?.[0]}
                  id={section?.management_section?.title?.replace(/\s/g, '')}
                />
              )
            }

            if (section?.data_section) {
              return (
                <FundPerformance
                  key={uniqueKey}
                  index={index}
                  section_title={section.data_section.title}
                  data_section={section.data_section}
                  pageContext={pageContext}
                  fund_classes={data.fund_classes}
                  productType={data.product_type}
                  id={section.data_section.title.replace(/\s/g, '')}
                  fundTitle={data.title}
                />
              )
            }

            if (section.documents_section) {
              return (
                <FundDocuments
                  key={uniqueKey}
                  index={index}
                  data={filteredProductDocuments}
                  documents_section={section.documents_section}
                  id={section.documents_section.title.replace(/\s/g, '')}
                />
              )
            }

            return null
          })}
          <Footnotes
            content={data.footnotes}
            activeShareClass={pageContext.fundClass}
          />
        </SectionsContainer>
      </ContentContainer>
    </Layout>
  )
}

export default FundDetailTemplateComponent
